import { useStoreActions, useStoreState } from '@/stores/store';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import SelectButton from '@/components/ui/therapist/SelectButton/SelectButton';
import CustomSlider from '@/components/SharedComponents/Slider/Slider';
import { getFullExerciseName } from '../../utils/exerciseNames';
import utils from '../../utils/utils';
import colors from '@/config/colors';
import SpeedDropdown from '@/components/SpeedDropdown';
import { Divider } from 'react-native-elements';
import { getPlaybackRate } from '@/utils/getPlaybackRate';

const styles = StyleSheet.create({
  subheading: { fontSize: 20, fontWeight: '600', marginBottom: 12 },
  selectContainer: { flexDirection: 'row', gap: 32, alignItems: 'flex-end' },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: colors.newUI.therapist.primaryBlue10,
    marginVertical: 8,
  },
});

function OralNaming() {
  const updateSettings = useStoreActions((actions) => actions.updateSettings);
  const copyWordsetExerciseTypes = useStoreState(
    (state) => state.copyWordsetExerciseTypes,
  );
  const copyTextsetExerciseTypes = useStoreState(
    (state) => state.copyTextsetExerciseTypes,
    (prev, next) => {
      if (
        prev[2]?.value !== next[2]?.value ||
        prev[2]?.playbackRate !== next[2]?.playbackRate
      ) {
        return false;
      } else {
        return true;
      }
    },
  );
  const copySentencesetExerciseTypes = useStoreState(
    (state) => state.copySentencesetExerciseTypes,
    (prev, next) => {
      if (
        prev[1]?.setting !== next[1]?.setting ||
        prev[1]?.value !== next[1]?.value ||
        prev[1]?.playbackRate !== next[1]?.playbackRate
      ) {
        return false;
      } else {
        return true;
      }
    },
  );

  return (
    <View>
      <View style={{ padding: 20 }}>
        <Text style={styles.subheading}>Wörter</Text>
        <View style={styles.selectContainer}>
          <View>
            <SelectButton
              options={[
                { label: 'Ohne Audioaufnahme', value: 'norecord' },
                { label: 'Mit Audioaufnahme', value: 'record' },
              ]}
              value={
                copyWordsetExerciseTypes.find(
                  ({ name }) =>
                    name === getFullExerciseName('Speaking', 'wordset'),
                )?.setting
              }
              onValueChange={(newValue) =>
                updateSettings({
                  newValue,
                  setType: 'wordset',
                  exerciseName: getFullExerciseName('Speaking', 'wordset'),
                  fieldToBeUpdated: 'setting',
                })
              }
            />
          </View>
          <View style={{ flex: 1 }}>
            <View>
              <CustomSlider
                title="Aufnahmedauer"
                valueUnit="Sekunden"
                onSlidingComplete={(value) =>
                  updateSettings({
                    newValue: value,
                    setType: 'wordset',
                    exerciseName: getFullExerciseName('Speaking', 'wordset'),
                    fieldToBeUpdated: 'value',
                  })
                }
                initialValue={utils.determineSettings(
                  copyWordsetExerciseTypes,
                  getFullExerciseName('Speaking', 'wordset'),
                  'word',
                  'setting',
                  'record',
                  'recordingDuration',
                )}
              />
            </View>
          </View>
        </View>
        <SpeedDropdown
          updateSettings={updateSettings}
          value={String(
            getPlaybackRate(copyWordsetExerciseTypes, 'Speaking', 'wordset'),
          )}
          exerciseName="Speaking"
          setType="wordset"
        />
      </View>
      <Divider width={1} color={colors.newUI.therapist.primaryBlue10} />
      <View style={{ padding: 20 }}>
        <Text style={styles.subheading}>Sätze</Text>
        <View style={styles.selectContainer}>
          <View>
            <SelectButton
              options={[
                { label: 'Ohne Audioaufnahme', value: 'norecord' },
                { label: 'Mit Audioaufnahme', value: 'record' },
              ]}
              value={
                copySentencesetExerciseTypes.find(
                  ({ name }) =>
                    name === getFullExerciseName('Speaking', 'sentenceset'),
                )?.setting
              }
              onValueChange={(newValue) =>
                updateSettings({
                  newValue,
                  setType: 'sentenceset',
                  exerciseName: getFullExerciseName('Speaking', 'sentenceset'),
                  fieldToBeUpdated: 'setting',
                })
              }
            />
          </View>
          <View style={{ flex: 1 }}>
            <View>
              <CustomSlider
                title="Aufnahmedauer"
                valueUnit="Sekunden"
                onSlidingComplete={(value) =>
                  updateSettings({
                    newValue: value,
                    setType: 'sentenceset',
                    exerciseName: getFullExerciseName(
                      'Speaking',
                      'sentenceset',
                    ),
                    fieldToBeUpdated: 'value',
                  })
                }
                initialValue={utils.determineSettings(
                  copySentencesetExerciseTypes,
                  getFullExerciseName('Speaking', 'sentenceset'),
                  'sentence',
                  'setting',
                  'record',
                  'recordingDuration',
                )}
              />
            </View>
          </View>
        </View>
        <SpeedDropdown
          updateSettings={updateSettings}
          value={String(
            getPlaybackRate(
              copySentencesetExerciseTypes,
              'Speaking',
              'sentenceset',
            ),
          )}
          exerciseName="Speaking"
          setType="sentenceset"
        />
      </View>
      <Divider width={1} color={colors.newUI.therapist.primaryBlue10} />
      <View style={{ padding: 20 }}>
        <Text style={styles.subheading}>Texte</Text>
        <View style={styles.selectContainer}>
          <View style={{ flex: 1 }}>
            <SelectButton
              options={[{ label: 'W-Fragen geleitet', value: 'questions' }]}
              value={
                copyTextsetExerciseTypes.find(
                  ({ name }) =>
                    name === getFullExerciseName('Speaking', 'textset'),
                )?.setting
              }
              onValueChange={(newValue) =>
                updateSettings({
                  newValue,
                  setType: 'textset',
                  exerciseName: getFullExerciseName('Speaking', 'textset'),
                  fieldToBeUpdated: 'setting',
                })
              }
            />
          </View>
        </View>
        <SpeedDropdown
          updateSettings={updateSettings}
          value={String(
            getPlaybackRate(copyTextsetExerciseTypes, 'Speaking', 'textset'),
          )}
          exerciseName="Speaking"
          setType="textset"
          dropdownPosition="top"
        />
      </View>
    </View>
  );
}

export default OralNaming;
